import React from "react";
import { findDOMNode } from "react-dom";
import { connect } from "react-redux";
import { createSelector, createStructuredSelector } from "reselect";
import {
  extent,
  scaleTime,
  scaleLinear,
  line,
  area,
  curveMonotoneX,
  isoParse,
  timeParse,
  bisector,
  format,
  timeFormat,
  zip,
  formatDefaultLocale,
} from "d3";
import Transition from "join-transition";

import "./style.css";

const data = (_, props) => props.data;
const before = (_, props) => props.before;
const after = (_, props) => props.after;
const x = (_, props) => props.x;
const fan = (_, props) => Array.isArray(props.y)
const y = createSelector(
  [(_, props) => props.y, fan],
  (y, fan) => fan ? y[Math.floor(y.length / 2)] : y
)
const yVars = createSelector(
  [(_, props) => props.y, fan],
  (y, fan) => fan ? y : []
)
const baseline = (_, props) => props.baseline || 0;
const showBars = (_, props) => props.showBars;

const width = (_, props) => props.width;
const reportWidth = (_, props) => props.reportWidth;
const split = (_, props) => props.split || false;
const gutter = state => state.gutter || 0;

const yAxisRight = (_, props) => props.yAxisRight || false
const retirement = (_, props) => props.retirement || null

const benchmarks = (_, props) => props.benchmarks || []

const isVertical = createSelector(
  [(_, props) => props.isVertical, reportWidth],
  (isVertical, reportWidth) => isVertical || reportWidth < 729
);

const size = createSelector(
  [width, isVertical],
  (width, isVertical) => (isVertical ? [width, width] : [width, width / 2])
);
const sideSize = createSelector(
  [size, split, gutter, isVertical],
  (size, split, gutter, isVertical) =>
    isVertical
      ? [size[0], (size[1] - gutter * split) / (1 + split)]
      : [(size[0] - gutter * split) / (1 + split), size[1]]
);

const margin = (_, props) => ({ left: 40, top: 100, bottom: 20, right: 40, ...(props.margin || {}) });

const dataByLine = createSelector(
  [data, before, benchmarks, after, x],
  (data, before_name, benchmarks, after_name, x) => {
    const before = [];
    const after = [];
    if (after_name == null) {
      return { before: data };
    }
    data.forEach(d => {
      if (d.type_tag === before_name) {
        before.push(d);
      } else if (d.type_tag === after_name) {
        after.push(d);
      }
    });
    return { before, after, benchmarks };
  }
);

const xFormat = createSelector(
  [(_, props) => props.xFormat, reportWidth], 
  (xFormat, reportWidth) =>
    !!xFormat && reportWidth < 729 ? xFormat.replace("%Y","'%y") : xFormat
)

const xScale = createSelector(
  [data, x, sideSize, margin, showBars],
  (data, x, size, margin, showBars) => {
    const accessor = d => isoParse(d[x]);
    let domain = extent(data, accessor);
    if (showBars) {
      const interval = Math.max(
        Math.abs(accessor(data[1]) - accessor(data[0])),
        Math.abs(accessor(data[data.length - 1]) - accessor(data[data.length - 2]))
      );
      domain = [
        new Date(+domain[0] - interval / 2),
        new Date(+domain[1] + interval / 2)
      ];
    }
    return scaleTime()
      .domain(domain)
      .range([margin.left || 0, size[0] - (margin.right || 0)]);
  }
);

const yScale = createSelector(
  [data, y, sideSize, margin, baseline, fan, yVars, split, benchmarks],
  (data, y, size, margin, baseline, fan, yVars, split, benchmarks) =>
    scaleLinear()
      .domain(fan 
        ? split > 0.5 //when chart has probability fan, include 5/95 values
          ? extent([
              ...data.map(d => d[yVars[Math.floor(yVars.length / 2) - 2]]),
              ...data.map(d => d[yVars[Math.floor(yVars.length / 2) + 2]]),
            ])
          : extent([
              baseline,
              ...data.map(d => d[yVars[Math.floor(yVars.length / 2)]])
            ])
        : benchmarks.length
          ? extent([
              baseline,
              ...data.map(d => d[y]),
              ...benchmarks.reduce((t,b) => [...t, ...b.map(d => d[y])],[])
            ])
          : extent([baseline, ...data.map(d => d[y])]))
      .nice(4)
      .clamp(true)
      .range([size[1] - (margin.bottom || 0), margin.top || 0])
);

const generateLine = createSelector(
  [x, y, xScale, yScale],
  (x, y, xScale, yScale) =>
    line()
      .x(d => xScale(isoParse(d[x])))
      .y(d => yScale(d[y]))
      .curve(curveMonotoneX)
);

const generateArea = createSelector(
  [x, y, xScale, yScale, baseline, fan, yVars],
  (x, y, xScale, yScale, baseline, fan, yVars) => {
    const f = area()
      .x(d => xScale(isoParse(d[x])))
      .curve(curveMonotoneX)
    return offset => f
      .y1(d => fan 
        ? yScale(d[yVars[Math.floor(yVars.length / 2) + offset]])
        : yScale(d[y]))
      .y0(d => fan 
        ? yScale(d[yVars[Math.floor(yVars.length / 2) - offset]])
        : yScale(baseline))
  }
);

const lines = createSelector(
  [dataByLine, generateLine, generateArea],
  (dataByLine, generateLine, generateArea) => ({
    before: {
      line: generateLine(dataByLine.before),
      area: generateArea(1)(dataByLine.before),
      area2: generateArea(2)(dataByLine.before),
    },
    after: dataByLine.after && {
      line: generateLine(dataByLine.after),
      area: generateArea(1)(dataByLine.after),
      area2: generateArea(2)(dataByLine.after),
    },
    benchmarks: dataByLine.benchmarks.length && dataByLine.benchmarks.map(b => ({
      line: generateLine(b)
    }) )
  })
);

const XAxis = ({ scale, ticks, hideMarks, neg, xFormat }) => 
  (<g className="axis x-axis">
    {(Array.isArray(ticks) ? ticks : scale.ticks(4)).map(tick => (
      <g key={tick} transform={`translate(${scale(tick)},0)`}>
        {hideMarks ? null : <line y2={neg ? -5 : 7} />}
        <text y={neg ? -16 : 8} textAnchor="middle" dy="1em">
          {scale.tickFormat(null,xFormat)(tick)}
        </text>
      </g>
    ))}
    <line x1={scale.range()[0]} x2={scale.range()[1]} />
  </g>);

const YAxis = ({ scale, gridWidth, format, yAxisRight }) => {
  const tickFormat = scale.tickFormat(4, format || ".1s");
  const yShift = yAxisRight ? gridWidth - 1 : 0
  return (
    <g className="axis y-axis">
      {scale.ticks(4).map(tick => (
        <g key={tick} transform={`translate(${yShift},${scale(tick)})`}>
          <line x2={(yAxisRight ? 1 : -1)*5} />
          <line x2={(yAxisRight ? -1 : 1)*gridWidth} className="grid" />
          <text x={yAxisRight ? 10 : -5} dx={-2} textAnchor={yAxisRight ? "start" : "end"} dy="0.35em">
            {tickFormat(tick)}
          </text>
        </g>
      ))}

      <line
        x1={yShift} x2={yShift}
        y1={scale.range()[0]} 
        y2={scale.range()[1]} strokeWidth={1} />
      {yAxisRight && <line className="grid"
        y1={scale.range()[0]} 
        y2={scale.range()[1]} />}
    </g>
  );
};

const LabelText = ({ translateX, translateY, fill, dy, dx, textAnchor, text, hideText }) => 
  (<g className="hovered label-text"
    transform={`translate(${translateX},${translateY})`}
    fill={fill}>
    {!hideText && <text className="timeline-label timeline-label-stroke"
      textAnchor={textAnchor} dy={dy} dx={dx}>{text}</text>}
    {!hideText && <text className="timeline-label"
      textAnchor={textAnchor} dy={dy} dx={dx}>{text}</text>}
    <circle r={3} strokeWidth={1.5} stroke="white" />
  </g>)

const Timeline = props => {

  // Set up the default local format - important to set the currency symbol here
  formatDefaultLocale({
    "decimal": ".",
    "thousands": ",",
    "grouping": [3],
    "currency": [props.TEXT.entityCCYSymbol, ""]
  })
  const formatValue = format(props.detailFormat || props.yFormat || ",.2f");
  const { hovered, hoveredPath } = props;
  const showFanOnHover = hoveredPath === props.beforeSide // when not split, detect which line was hovered over
  const opacityCalc = o => showFanOnHover ? o : o*props.split
  const leftOfHalf = props.xScale(isoParse(hovered[props.x])) < 0.5*(props.xScale.range()[1] - props.xScale.range()[0])
  const lastValueHover = hovered[props.x] && isoParse(hovered[props.x]).toString() === props.xScale.domain()[1].toString() //when on last value in timeline
  
  return (
    <g className="timeline">
      {props.title && <text y={30} className="title">{props.title}</text>}

      {(props.showArea || showFanOnHover)
        ? <path
            d={props.line.area}
            fill={props.color}
            fillOpacity={opacityCalc(0.1)}
          />
        : null}

      {props.fan && (props.showArea || showFanOnHover)
        ? <path
            d={props.line.area2}
            fill={props.color}
            fillOpacity={opacityCalc(0.1)}
          />
        : null}

      {props.fan && (props.showArea || showFanOnHover)
        //legend
        ? <g 
          className={"fan-legend "+(props.beforeSide ? "original" : "proposed")} 
          transform={`translate(${20},${70})`}
          fillOpacity={opacityCalc(1)}>
          <text y={9}>{props.TEXT.probabilityLabel+" "}</text>
          <line x1={55} x2={70} y1={5} y2={5} stroke={props.color} strokeOpacity={opacityCalc(1)} />
          <text x={72} y={9}>50%</text>
          <rect x={95} height={10} width={10} fill={props.color} fillOpacity={opacityCalc(.4)}></rect>
          <text x={107} y={9}>10%/90%</text>
          <rect x={155} height={10} width={10} fill={props.color} fillOpacity={opacityCalc(.2)}></rect>
          <text x={167} y={9}>5%/95%</text>
        </g>
        : null}

      {props.fan && props.retirement
        ? <line className="retirement"
            x1={props.xScale(isoParse(props.retirement[props.x])) - 1}
            x2={props.xScale(isoParse(props.retirement[props.x])) - 1}
            y1={props.yScale.range()[0]}
            y2={props.yScale.range()[1]} />
        : null}

      <path
        d={props.line.line}
        strokeOpacity={Math.max(+!props.showBars, props.split || 0)}
        fill="none"
        stroke={props.showBars ? "#BCC2C6" : props.color}
        strokeWidth={props.benchmark ? 1 : 2}
        strokeDasharray={props.benchmark ? `${8 / props.benchmark} ${8 / props.benchmark}` : ''} 
      />

      {props.fan && (props.split || showFanOnHover) //when on last value in timeline
        //point & label for 5/10/90/95%
        ? <g className="fan-hover">
            <line strokeDasharray="4"
              x1={props.xScale(isoParse(hovered[props.x])) - 1} 
              x2={props.xScale(isoParse(hovered[props.x])) - 1}
              y1={props.yScale(hovered[props.yVars[0]])}
              y2={props.yScale(0)} />
            <line strokeDasharray="4"
              x1={props.xScale(isoParse(hovered[props.x])) - 1} 
              x2={props.xScale(isoParse(hovered[props.x])) - 1} 
              y1={props.yScale(hovered[props.yVars[props.yVars.length - 1]])}
              y2={props.yScale.range()[1]} />
            <line
              x1={props.xScale(isoParse(hovered[props.x])) - 1} 
              x2={props.xScale(isoParse(hovered[props.x])) - 1} 
              y1={props.yScale(hovered[props.yVars[0]])} 
              y2={props.yScale(hovered[props.yVars[props.yVars.length - 1]])} />
            {[  ...props.yVars.slice(0, Math.floor(props.yVars.length/2)), 
                ...props.yVars.slice(Math.floor(props.yVars.length/2) + 1) ].map((d,i,a) => 
              <LabelText key={d}
                hideText={!lastValueHover}
                translateX={hovered[d] !== undefined
                  ? props.xScale(isoParse(hovered[props.x])) + (props.showBars && !props.split ? (props.beforeSide ? -7.5 : 7.5) : 0)
                  : props.margin.left} 
                translateY={hovered[d] !== undefined
                  ? props.yScale(hovered[d])
                  : props.margin.top} 
                fill={props.color}
                dy={i >= Math.floor(a.length/2) ? -7 : 10}
                dx={props.showBars && !props.split
                  ? props.beforeSide ? -12.5 : 12.5
                  : leftOfHalf
                    ? 5
                    : -5}
                textAnchor={leftOfHalf ? 'start' : 'end'}
                text={formatValue(hovered[d]).replace(/\.$/, "")} />)}
            <text 
              className="hover-year"
              x={props.xScale(isoParse(hovered[props.x]))}
              y={props.yScale(0) + 10}>
              {timeFormat("%Y")(isoParse(hovered.date))}
            </text>
          </g>
        : null}

      {hovered && !props.totalBar && //when on last value in timeline
        //point & label for 50%
        <LabelText 
          hideText={props.split && !lastValueHover}
          translateX={hovered[props.y] !== undefined
            ? props.xScale(isoParse(hovered[props.x])) + (props.showBars && !props.split ? (props.beforeSide ? -7.5 : 7.5) : 0)
            : props.margin.left} 
          translateY={hovered[props.y] !== undefined
            ? props.yScale(hovered[props.y])
            : props.margin.top} 
          fill={props.color} 
          dy={props.showBars || props.split || props.higher ? -7 : 14} 
          dx={props.showBars && !props.split
            ? props.beforeSide ? -12.5 : 12.5
            : leftOfHalf
              ? 5
              : -5}
          textAnchor={props.showBars 
            ? 'middle'
            : leftOfHalf 
              ? 'start' 
              : 'end'}
          text={props.benchmark 
            ? `${props.hovered.name} ${formatValue(hovered[props.y])}`
            : !isNaN(hovered[props.y]) && formatValue(hovered[props.y]).replace(/\.$/, "")}
        />}

      {props.fan && (props.split || showFanOnHover) && !lastValueHover //not on last value 
        //tooltip box
        ? <g className="fan-tooltip"
            transform={`translate(${props.xScale(isoParse(hovered[props.x])) - (leftOfHalf ? -10 : 150)},${props.yScale(hovered[props.y]) - 50})`}>
            <path stroke={props.color} 
              d={`M0,0${leftOfHalf 
                ? 'L0,45L-5,50L0,55' 
                : ''}L0,100L140,100${leftOfHalf 
                ? ''
                : 'L140,55L145,50L140,45'}L140,0Z`} />
            <g transform="translate(5,10)">
              <line x1="0" x2="10" stroke={props.color}/>
              <text y="3" x="12">Average</text>
              <text className="bold filled" y="3" x="130" textAnchor="end" fill={props.color}>
                {formatValue(hovered[props.y])}
              </text>
            </g>
            <text className="bold" x="5" y="30">Chance of having at least:</text>
            {zip([  ...props.yVars.slice(0, Math.floor(props.yVars.length/2)), 
                ...props.yVars.slice(Math.floor(props.yVars.length/2) + 1) ],
                ['95%','90%','10%','5%']).reverse().map((l,li) => 
              <g key={'tooltip-g-'+li} transform={`translate(5,${40 + li*15})`}>
                <rect 
                  height="10" 
                  width="10" 
                  fill={props.color} 
                  opacity={li === 0 || li === props.yVars.length - 2 ? 0.2 : 0.4} />
                <text y="10" x="12">{l[1]}</text>
                <text y="10" x="130" textAnchor="end">{formatValue(hovered[l[0]])}</text>
              </g>)}
          </g>
        : null}

      {props.showBars &&
        props.data.map(d => {
          const center = (props.xScale.range()[1] - props.xScale.range()[0]) / 2;
          const y = props.yScale(d[props.y]);
          const domain = props.xScale.domain();
          const totalBarW = 1.5*center;
          return props.totalBar
            ? <g key={d[props.x]}>
                <rect
                  x={
                    props.xScale.range()[0] +
                      center -
                      Math.max(
                        0.5 * totalBarW * props.split,
                        0.5 * totalBarW * props.beforeSide
                      )
                  }
                  width={(props.totalBar ? totalBarW : 30) / (2 - props.split)}
                  y={y}
                  height={props.yScale.range()[0] - y}
                  fill={props.color}
                />
                <text
                  className="text"
                  x={
                    props.xScale.range()[0] +
                      center +
                      (1 - props.split) *
                        (-0.75 * totalBarW +
                          0.5 * totalBarW * (2 - props.beforeSide))
                  }
                  y={props.yScale(0.5 * d[props.y]) + 10}
                >
                  {d[props.y] === 0 ? "" : props.TEXT.entityCCYSymbol + formatValue(d[props.y])}
                </text>
                <g className="axis x-axis center">
                  <text
                    x={
                      props.xScale.range()[0] +
                        center +
                        (1 - props.split) *
                          (-0.75 * totalBarW +
                            0.5 * totalBarW * (2 - props.beforeSide))
                    }
                    y={props.yScale(0) + 15}
                  >
                    {d[props.xLabel]}
                  </text>
                </g>
              </g>
            : <g key={d[props.x]}>
                <rect
                  x={(domain[0] === domain[domain.length - 1]
                      ? props.xScale.range()[0] + center
                      : props.xScale(isoParse(d[props.x])))
                    - Math.max(15 * props.split, 15 * props.beforeSide)
                  }
                  width={30 / (2 - props.split)}
                  y={y}
                  height={props.yScale.range()[0] - y}
                  fill={props.color}
                />
              </g>;
        })}


    </g>
  );
};

class Timelines extends React.Component {
  state = { hovered: null };
  render() {
    const {
      lines,
      dataByLine,
      x,
      y,
      xScale,
      xFormat,
      yFormat,
      colorScale,
      size,
      sideSize,
      isVertical,
      gutter,
      split,
      title,
      showBars,
      totalBar,
      fan,
      yAxisRight,
      detailFormat,
      TEXT,
      originalPortfolioLabel,
      proposedPortfolioLabel,
      originalPortfolioShortLabel,
    } = this.props;

    const hovered = showBars && this.state.hovered == null
      ? []
      : [dataByLine.before, dataByLine.after, ...dataByLine.benchmarks].map(data => 
        data[
          this.state.hovered != null
            ? Math.max(
                0,
                Math.min(
                  data.length,
                  bisector(d => +isoParse(d[x])).right(
                    data,
                    this.state.hovered
                  )
                ) - 1
              )
            : data.length - 1
        ]);
    let leftOfHalf
    if(fan && hovered[0]){
      leftOfHalf = xScale(isoParse(hovered[0][x])) < 0.5*(xScale.range()[1] - xScale.range()[0])
    }

    const lineHeight = 14;
    const lineLength = 50;

    // Set up the location of the labels
    const originalPortfolioLabelStartXPos = 0; 
    const originalPortfolioLabelStartYPos = 0;
    const originalPortfolioLabelEndXPos = originalPortfolioShortLabel.length > lineLength ? 0 : null;
    const originalPortfolioLabelEndYPos = originalPortfolioShortLabel.length > lineLength ? lineHeight : null;

    const proposedPortfolioLabelStartXPos = originalPortfolioShortLabel.length + proposedPortfolioLabel.length > lineLength ? 0 : null;
    const proposedPortfolioLabelStartYPos = originalPortfolioShortLabel.length + proposedPortfolioLabel.length > lineLength ? lineHeight : 0;
    const proposedPortfolioLabelEndXPos = proposedPortfolioLabel.length > lineLength ? 0 : null;
    const proposedPortfolioLabelEndYPos = proposedPortfolioLabel.length > lineLength ? lineHeight : proposedPortfolioLabelStartYPos; //proposedPortfolioLabel.length > lineLength ? proposedPortfolioLabelStartYPos + lineHeight : proposedPortfolioLabelStartYPos;

    // Set up the original label names - chop them in half if necessary
    var originalNameLabelStart = originalPortfolioShortLabel.length > lineLength ? originalPortfolioShortLabel.substring(0, lineLength - 1) : originalPortfolioShortLabel;
    // Set up the second line
    var originalNameLabelEnd = originalPortfolioShortLabel.length > lineLength ? originalPortfolioShortLabel.substring(lineLength - 1, originalPortfolioShortLabel.length) : 
        null;
    // Add a '-' if any words in start are chopped in half
    originalNameLabelStart = originalNameLabelStart[originalNameLabelStart.length -1] !== ' ' && 
        originalPortfolioShortLabel.length > lineLength  && 
        originalNameLabelEnd && originalNameLabelEnd.substring(0, 1) !== ' '? 
        originalNameLabelStart + '-' : originalNameLabelStart;

    // Strip out the first space if one exists
    originalNameLabelEnd = originalNameLabelEnd && originalNameLabelEnd.substring(0, 1) === ' ' ? originalNameLabelEnd.substring(1, originalNameLabelEnd.length) : originalNameLabelEnd;

    // Set up the proposed label names - chop them in half if necessary
    var proposedNameLabelStart = proposedPortfolioLabel.length > lineLength ? proposedPortfolioLabel.substring(0, lineLength - 1) : proposedPortfolioLabel;
    // Set up the second line
    var proposedNameLabelEnd = proposedPortfolioLabel.length > lineLength ? proposedPortfolioLabel.substring(lineLength - 1, proposedPortfolioLabel.length) : 
      null;
    // Add a '-' if any words in start are chopped in half
    proposedNameLabelStart = proposedNameLabelStart[proposedNameLabelStart.length -1] !== ' ' && 
      proposedPortfolioLabel.length > lineLength  && 
      proposedNameLabelEnd && proposedNameLabelEnd.substring(0, 1) !== ' '? 
        proposedNameLabelStart + '-' : proposedNameLabelStart;
    // Strip out the first space if one exists
    proposedNameLabelEnd = proposedNameLabelEnd && proposedNameLabelEnd.substring(0, 1) === ' ' ? proposedNameLabelEnd.substring(1, proposedNameLabelEnd.length) : proposedNameLabelEnd;

    return (
      <Transition values={this.props.yScale.domain()[1]} duration={500}>{domainMax => { 
      const yScale = this.props.yScale.domain([this.props.yScale.domain()[0], domainMax]);
      return (<svg
        width={size[0]}
        height={size[1]}
        onMouseMove={event => {
          const {left, top} = findDOMNode(this).getBoundingClientRect();
          const hoveredPosition = showBars
            ? +new Date(
                xScale.invert(
                  (event.clientX - left + 15) % (sideSize[0] + gutter)
                )
              )
            : isVertical
              ? +new Date(xScale.invert(event.clientX - left))
              : +new Date(xScale.invert((event.clientX - left) % (sideSize[0] + gutter)));

          let hoveredY, hoveredObj
          if(!split && !showBars){ // when two lines are on the same axes, trigger hover when mouse Y-position is within a given margin
            hoveredY = event.clientY - top
            hoveredObj = [dataByLine.before, dataByLine.after]
              .map(data => data.filter(d => timeFormat("%Y")(timeParse("%Y-%m-%dT%H:%M:%S")(d[x])) === timeFormat("%Y")(isoParse(xScale.invert((event.clientX - left) % (sideSize[0] + gutter))))))
              .map(d => yScale(d[0][y]))
          }

          this.setState({
            hovered: hoveredPosition,
            hoveredPath: split || showBars ? null
              : Math.abs(hoveredObj[1] - hoveredY) < 7 // this is the given margin
                ? 0
                : Math.abs(hoveredObj[0] - hoveredY) < 7
                  ? 1
                  : null
          });
        }}
        onMouseLeave={() => this.setState({ hovered: null })}
      >
        <g>
          <g>
            {totalBar
              ? null
              : <g transform={`translate(${xScale.range()[0]},0)`}>
                  <YAxis
                    scale={yScale}
                    yAxisRight={yAxisRight}
                    gridWidth={xScale.range()[1] - xScale.range()[0]}
                    format={yFormat} />
                </g>}

            <g transform={`translate(0,${yScale(0)})`}>
              <XAxis
                scale={xScale}
                hideMarks={showBars}
                neg={yScale(0) === yScale.range()[1]}
                ticks={showBars 
                  ? split
                    ? dataByLine["before"].length
                    : Math.max(dataByLine["before"].length, dataByLine["after"].length) 
                  : null}
                xFormat={xFormat}
              />
            </g>
          </g>
          {lines.after && split !== 0 &&
            <g transform={`translate(${(gutter + sideSize[0]) * !isVertical * split},${(gutter + sideSize[1]) * isVertical * split})`}>
              {totalBar
                ? null
                : <g transform={`translate(${xScale.range()[0]},0)`}>
                    <YAxis
                      scale={yScale}
                      yAxisRight={yAxisRight}
                      gridWidth={xScale.range()[1] - xScale.range()[0]}
                      format={yFormat} />
                  </g>}

              <g transform={`translate(0,${yScale(0)})`}>
                <XAxis
                  scale={xScale}
                  hideMarks={showBars}
                  neg={yScale(0) === yScale.range()[1]}
                  ticks={showBars ? dataByLine["after"].length : null}
                  xFormat={xFormat}
                />
              </g>
            </g>}

          {fan && !split && hovered && this.state.hoveredPath === null
            // unsplit delta value and line
            ? <g className="unsplit-delta-lines"
                transform={`translate(${xScale(isoParse(hovered[0][x])) - 1},0)`}>
                <line strokeDasharray="4"
                  x1={0} 
                  x2={0}
                  y1={yScale.range()[1]}
                  y2={yScale(hovered[0][y])} />
                <line strokeDasharray="4"
                  x1={0} 
                  x2={0} 
                  y1={yScale(hovered[1][y])}
                  y2={yScale(0)} />
                <text className="label-stroke"
                  textAnchor={leftOfHalf
                    ? 'start'
                    : 'end'}
                  x={leftOfHalf
                    ? 5
                    : -5}
                  y={5 + 0.5*(yScale(hovered[0][y]) + yScale(hovered[1][y]))}>
                  {format(detailFormat)(hovered[1][y] - hovered[0][y])}
                </text>
                <text 
                  textAnchor={leftOfHalf
                    ? 'start'
                    : 'end'}
                  x={leftOfHalf
                    ? 5
                    : -5}
                  y={5 + 0.5*(yScale(hovered[0][y]) + yScale(hovered[1][y]))}>
                  {format(detailFormat)(hovered[1][y] - hovered[0][y])}
                </text>
                <text
                  className="hover-year"
                  y={yScale(0) + 10}>
                  {timeFormat("%Y")(isoParse(hovered[0].date))}
                </text>
              </g>
            : null}

          <Timeline
            {...this.props}
            yScale={yScale.domain([yScale.domain()[0], domainMax])}
            beforeSide={1}
            data={dataByLine["before"]}
            line={lines["before"]}
            hovered={hovered.length && hovered[0]}
            hoveredPath={fan ? this.state.hoveredPath : null}
            higher={!showBars && hovered.length && hovered[0][y] >= hovered[1][y]}
            color={colorScale("before")}
            title={
              title != null
                ? title
                : split > 0.025
                    ? <tspan className="original version-heading">
                        <tspan 
                          dy={originalPortfolioLabelStartYPos} 
                          x={originalPortfolioLabelStartXPos} 
                          className="original">{originalPortfolioLabel === TEXT.originalPortfolioLabel ? TEXT.originalPortfolioLabel : originalNameLabelStart }
                        </tspan>
                        <tspan 
                          dy={originalPortfolioLabelEndYPos} 
                          x={originalPortfolioLabelEndXPos} 
                          className="original">{originalNameLabelEnd ? originalNameLabelEnd : null}
                        </tspan>
                      </tspan>
                    : <tspan className="version-heading">
                        <tspan 
                          dy={originalPortfolioLabelStartYPos} 
                          x={originalPortfolioLabelStartXPos} 
                          className="original">{originalNameLabelStart ? originalNameLabelStart : TEXT.originalPortfolioLabel.split(" ")[0]}
                        </tspan>
                        <tspan 
                          dy={originalPortfolioLabelEndYPos} 
                          x={originalPortfolioLabelEndXPos} 
                          className="original">{originalNameLabelEnd ? originalNameLabelEnd : null}
                        </tspan>
                        {" "}
                        vs.
                        {" "}
                        <tspan 
                          dy={proposedPortfolioLabelStartYPos} 
                          x={proposedPortfolioLabelStartXPos} 
                          className="proposed">{proposedNameLabelStart ? proposedNameLabelStart : TEXT.proposedPortfolioLabel}
                        </tspan>
                        <tspan 
                          dy={proposedPortfolioLabelEndYPos} 
                          x={proposedPortfolioLabelEndXPos} 
                          className="proposed">{proposedNameLabelEnd ? proposedNameLabelEnd : null}
                        </tspan>
                      </tspan>
            } 
          />
        </g>

        {lines.after &&
          <g
            transform={`translate(${(gutter + sideSize[0]) * !isVertical * split},${(gutter + sideSize[1]) * isVertical * split})`}
          >
            {!split || !isVertical
              ? null
              : <line
                  className="dotted-separator"
                  x1="0"
                  x2={xScale.range()[1]}
                />}
            <Timeline
              {...this.props}
              yScale={yScale.domain([yScale.domain()[0], domainMax])}
              beforeSide={0}
              data={dataByLine["after"]}
              line={lines["after"]}
              hovered={hovered.length && hovered[1]}
              hoveredPath={fan ? this.state.hoveredPath : null}
              higher={!showBars && hovered.length && hovered[0][y] < hovered[1][y]}
              color={colorScale("after")}
              title={
                title != null
                  ? title
                  : split > 0.05
                      ? <tspan className="proposed version-heading">
                        <tspan 
                            dy={proposedPortfolioLabelStartYPos} 
                            x={proposedPortfolioLabelStartXPos} 
                            className="proposed">{proposedNameLabelStart ? proposedNameLabelStart : TEXT.proposedPortfolioLabel}
                          </tspan>
                          <tspan 
                            dy={proposedPortfolioLabelEndYPos} 
                            x={proposedPortfolioLabelEndXPos} 
                            className="proposed">{proposedNameLabelEnd ? proposedNameLabelEnd : null}
                          </tspan>                            
                        </tspan>
                      : null
              }
            />
          </g>}

        {lines.benchmarks.length && !split 
          ? lines.benchmarks.map((b,bi) => 
              <Timeline 
                {...this.props}
                key={'benchmark-'+bi}
                benchmark={bi + 1} // make truthy, use as divisor later on
                yScale={yScale.domain([yScale.domain()[0], domainMax])}
                beforeSide={0}
                hovered={hovered.length && hovered[bi+2]} // 0 is original, 1 is proposed
                hoveredPath={null}
                data={dataByLine["benchmarks"][bi]}
                line={lines["benchmarks"][bi]}
                color={'#888'}
              />)
          : null}

      </svg>)
      }}</Transition>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  lines,
  dataByLine,
  x,
  y,
  xFormat,
  xScale,
  yScale,
  size,
  sideSize,
  isVertical,
  split,
  showArea: (state, props) =>
    props.showArea != null ? props.showArea : !!split(state, props),
  gutter,
  margin,
  fan,
  yVars,
  yAxisRight,
  retirement,
  TEXT: state => state.complianceText,
});

export default connect(mapStateToProps)(Timelines);



// WEBPACK FOOTER //
// ./src/Timeline/index.js